/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import React from 'react';
import { inject, observer } from 'mobx-react';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { shapes, LoadingHandler, StateErrorDisplay } from '@greenville/framework';
import {
  Typography,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import Books from '../../models/Books';
import ContentBundle from '../../models/ContentBundle';
import Breadcrumbs from '../commonitems/Breadcrumbs';
import Loader from '../../../../common/Loader';
import UsersPermissions from '../../../users/models/UsersPermissions';
import NotificationModel from '../../../../common/components/notification/NotificationModel';
import utils from '../../../../common/utils';
import env from '../../../../common/env';

const styles = (theme) => ({
  root: {
    width: '99%',
    padding: theme.spacing(3)
  },
  paper: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(2)
  },
  title: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    textAlign: 'center'
  },
  subtitle: {
    marginBottom: theme.spacing(3),
    fontStyle: 'italic',
    color: '#666'
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0'
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    fontWeight: 600
  },
  inputField: {
    width: '100%',
    '& input': {
      fontSize: '14px',
      padding: '10px'
    }
  },
  addButton: {
    backgroundColor: '#005d83',
    color: '#fff',
    '&:hover': {
      color: '#fff',
      backgroundColor: '#005d83'
    }
  },
  deleteButton: {
    backgroundColor: '#FF2400',
    color: '#fff',
    '&:hover': {
      color: '#fff',
      backgroundColor: '#FF2400'
    }
  },
  saveButton: {
    backgroundColor: '#005d83',
    color: '#fff',
    '&:hover': {
      color: '#fff',
      backgroundColor: '#005d83'
    }
  },
  exportButton: {
    backgroundColor: '#005d83',
    color: '#fff',
    marginRight: theme.spacing(2),
    '&:hover': {
      color: '#fff',
      backgroundColor: '#005d83'
    }
  },
  cancelButton: {
    marginRight: theme.spacing(2),
    backgroundColor: '#005d83',
    color: '#fff',
    '&:hover': {
      color: '#fff',
      backgroundColor: '#005d83'
    }
  },
  actionButtons: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between'
  },
  bundleIdField: {
    backgroundColor: '#f9f9f9'
  },
  dialog: {
    width: '450px',
    maxWidth: '450px'
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#005d83',
    color: '#fff',
    padding: theme.spacing(1.5, 2)
  },
  dialogTitleText: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#fff'
  },
  closeButton: {
    color: '#fff',
    marginLeft: 'auto',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)'
    }
  },
  dialogContentRoot: {
    backgroundColor: '#fff',
    padding: 0
  },
  tocList: {
    maxHeight: '500px',
    overflow: 'auto',
    padding: theme.spacing(2),
    backgroundColor: '#fff'
  },
  tocItem: {
    cursor: 'pointer',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  tocSubItem: {
    paddingLeft: theme.spacing(6),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  tocItemNumber: {
    color: '#1976d2',
    marginRight: theme.spacing(1),
    fontWeight: 500,
    fontSize: '16px'
  },
  tocItemText: {
    display: 'flex',
    alignItems: 'flex-start',
    '& > span': {
      fontSize: '16px',
      color: '#000'
    }
  },
  tocItemTextPrimary: {
    fontSize: '16px',
    color: '#000'
  }
});

@inject('booksState', 'booksStatus', 'userPermissions', 'contentBundleState', 'notificationState')
@observer
class ContentBundles extends React.Component {
  static propTypes = {
    match: PropTypes.object.isRequired,
    classes: PropTypes.object.isRequired,
    language: shapes.language.isRequired,
    booksState: shapes.modelOf(Books).isRequired,
    booksStatus: shapes.state.isRequired,
    userPermissions: shapes.modelOf(UsersPermissions).isRequired,
    contentBundleState: shapes.modelOf(ContentBundle).isRequired,
    notificationState: shapes.modelOf(NotificationModel).isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      bundles: [
        {
          id: 1,
          startPoint: '',
          endPoint: '',
          bundleName: '',
          bundleId: '',
          bundleUri: []
        }
      ],
      selectedFieldType: '', // 'start' or 'end'
      selectedBundleId: null,
      tocData: [],
      nextBundleId: 2, // Track next available ID
      lastUpdated: 0
    };
  }

  componentDidMount() {
    const { booksState, contentBundleState, match } = this.props;
    booksState.fetchBook(match.params.bookid);
    const { pulseBookID } = booksState.bookDetail;
    if (pulseBookID) {
      contentBundleState.fetchBundles(pulseBookID);
    }
  }

  componentDidUpdate(prevProps) {
    const { booksState, contentBundleState } = this.props;

    const prevPulseBookID = prevProps.booksState.bookDetail.pulseBookID;
    const nextPulseBookID = booksState.bookDetail.pulseBookID;
    if (nextPulseBookID && nextPulseBookID !== prevPulseBookID) {
      contentBundleState.fetchBundles(nextPulseBookID);
    }

    const nextBundles = contentBundleState.bundles;
    const { lastUpdated: localLastUpdated } = this.state;
    const storeLastUpdated = contentBundleState.lastUpdated;
    const storeJustUpdated = storeLastUpdated !== localLastUpdated;
    const storeLengthChanged = prevProps.contentBundleState.bundles.length !== nextBundles.length;
    if (storeJustUpdated || storeLengthChanged) {
      const dataRows = nextBundles.map((item, index) => {
        const uris = item.bundleUri && item.bundleUri.length > 0 ? item.bundleUri : [];
        const startPoint = item.startPointTitle;
        const endPoint = item.endPointTitle;
        return {
          id: index + 1,
          startPoint,
          endPoint,
          bundleName: (item.bundleLabel != null ? String(item.bundleLabel) : '') || '',
          bundleId: (item.bundleId != null ? String(item.bundleId) : '') || '',
          bundleUri: uris
        };
      });
      const emptyRow = {
        id: nextBundles.length + 1,
        startPoint: '',
        endPoint: '',
        bundleName: '',
        bundleId: '',
        bundleUri: []
      };
      this.setState({
        bundles: [...dataRows, emptyRow],
        nextBundleId: nextBundles.length + 2,
        lastUpdated: storeLastUpdated
      });
    }
  }

  handleAddBundle = () => {
    this.setState((prevState) => ({
      bundles: [
        ...prevState.bundles,
        {
          id: prevState.nextBundleId,
          startPoint: '',
          endPoint: '',
          bundleName: '',
          bundleId: '',
          bundleUri: []
        }
      ],
      nextBundleId: prevState.nextBundleId + 1
    }));
  };

  handleDeleteBundle = (id) => {
    const { contentBundleState, booksState } = this.props;
    const { bundles } = this.state;

    const bundle = bundles.find((b) => b.id === id);
    if (!bundle) return;

    const { pulseBookID } = booksState.bookDetail;
    if (bundle.bundleId) {
      contentBundleState.deleteBundle({ bookId: pulseBookID, bundleId: bundle.bundleId });
    }

    this.setState((prevState) => {
      const remaining = prevState.bundles.filter((b) => b.id !== id);
      const dataRows = remaining.filter((b) => b.bundleId || b.bundleName || b.startPoint);
      const existingEmptyRow = remaining.find((b) => !b.bundleId && !b.bundleName && !b.startPoint);
      const emptyRow = existingEmptyRow || {
        id: Date.now(),
        startPoint: '',
        endPoint: '',
        bundleName: '',
        bundleId: '',
        bundleUri: []
      };
      return {
        bundles: [...dataRows, emptyRow],
        nextBundleId: prevState.nextBundleId
      };
    });
  };

  handleInputChange = (id, field, value) => {
    const { bundles: prevBundles } = this.state;
    const previousBundleName =
      field === 'bundleName' ? (prevBundles.find((b) => b.id === id) || {}).bundleName : undefined;

    this.setState(
      (prevState) => ({
        bundles: prevState.bundles.map((bundle) => (bundle.id === id ? { ...bundle, [field]: value } : bundle))
      }),
      () => {
        const { bundles } = this.state;
        const currentBundle = bundles.find((b) => b.id === id);
        if ((field === 'startPoint' || field === 'endPoint') && currentBundle) {
          if (currentBundle.startPoint && currentBundle.endPoint) {
            const bundleUri = this.extractBundleUrl(currentBundle.startPoint, currentBundle.endPoint);
            this.setState((prevState) => ({
              bundles: prevState.bundles.map((b) => (b.id === id ? { ...b, bundleUri } : b))
            }));
          }
        }
        if (field === 'bundleName' && currentBundle) {
          const nameTrimmed = String(value || '').trim();
          if (!nameTrimmed) return;
          const isDuplicate = bundles.some(
            (b) =>
              b.id !== id &&
              String(b.bundleName || '')
                .trim()
                .toLowerCase() === nameTrimmed.toLowerCase()
          );
          if (isDuplicate) {
            const { notificationState } = this.props;
            notificationState.set({
              open: true,
              message: 'This bundle name already exists. Please enter a unique name.',
              type: 'warning'
            });
            const revertValue = currentBundle.bundleId ? previousBundleName : '';
            this.setState((prevState) => ({
              bundles: prevState.bundles.map((b) => (b.id === id ? { ...b, bundleName: revertValue } : b))
            }));
          }
        }
      }
    );
  };

  getPxePrefix = () => {
    const { booksState } = this.props;
    const { pulseBookID, version } = booksState.bookDetail;
    if (!pulseBookID || !version) return '';
    return `${env.PXE_READER_URL}/${pulseBookID}/${version}/`;
  };

  extractBundleUrl = (startPoint, endPoint) => {
    const { contentBundleState } = this.props;
    const tocData =
      contentBundleState.tocData && contentBundleState.tocData.length > 0 ? contentBundleState.tocData : [];

    const flatItems = [];
    tocData.forEach((item) => {
      flatItems.push({ href: item.href, playOrder: item.playOrder, title: item.title });
      if (item.children) {
        item.children.forEach((child) => {
          flatItems.push({ href: child.href, playOrder: child.playOrder, title: child.title });
        });
      }
    });

    const startItem = flatItems.find((item) => item.title === startPoint);
    const endItem = flatItems.find((item) => item.title === endPoint);
    if (!startItem || !endItem) return [];

    const startOrder = Math.min(startItem.playOrder, endItem.playOrder);
    const endOrder = Math.max(startItem.playOrder, endItem.playOrder);

    return flatItems
      .filter((item) => item.playOrder >= startOrder && item.playOrder <= endOrder)
      .map((item) => item.href);
  };

  handleOpenTocModal = (bundleId, fieldType) => {
    const { bundles } = this.state;
    // Validate if end point is being clicked without start point
    if (fieldType === 'end') {
      const bundle = bundles.find((b) => b.id === bundleId);
      if (!bundle.startPoint) {
        // eslint-disable-next-line no-alert
        window.alert('Pick a start point first');
        return;
      }
    }

    this.setState({
      tocModalOpen: true,
      selectedFieldType: fieldType,
      selectedBundleId: bundleId
    });
  };

  handleCloseTocModal = () => {
    this.setState({
      tocModalOpen: false,
      selectedFieldType: '',
      selectedBundleId: null
    });
  };

  handleSelectTocItem = (title) => {
    const { selectedBundleId, selectedFieldType } = this.state;

    if (selectedFieldType === 'start') {
      this.handleInputChange(selectedBundleId, 'startPoint', title);
    } else if (selectedFieldType === 'end') {
      this.handleInputChange(selectedBundleId, 'endPoint', title);
    }

    this.handleCloseTocModal();
  };

  getFullHref = (rawHref) => {
    const pxePrefix = this.getPxePrefix();
    if (!pxePrefix || rawHref.startsWith('http')) return rawHref;
    return `${pxePrefix}${rawHref}`;
  };

  buildBundlePayload = (bundle, bookDetail, bookid) => ({
    globalBookId: bookDetail.globalBookID || bookid,
    bookId: bookid,
    bundleLabel: bundle.bundleName,
    bundleUri: bundle.bundleUri && bundle.bundleUri.length > 0 ? bundle.bundleUri.map(this.getFullHref) : [],
    createdBy: bookDetail.modifiedBy || ''
  });

  buildBundleWithBundleIdPayload = (bundle, bookDetail, bookid) => ({
    globalBookId: bookDetail.globalBookID || bookid,
    bookId: bookid,
    bundleLabel: bundle.bundleName,
    bundleUri: bundle.bundleUri && bundle.bundleUri.length > 0 ? bundle.bundleUri.map(this.getFullHref) : [],
    createdBy: bookDetail.modifiedBy || '',
    bundleId: bundle.bundleId
  });

  isBundleValidToSave = (bundle) => {
    if (!bundle) return false;
    const name = String(bundle.bundleName || '').trim();
    return Boolean(bundle.startPoint && bundle.endPoint && name);
  };

  isBundleIncomplete = (bundle) => {
    if (!bundle) return false;
    const hasAnyData = bundle.startPoint || bundle.endPoint || String(bundle.bundleName || '').trim();
    return Boolean(hasAnyData && !this.isBundleValidToSave(bundle));
  };

  handleSaveBundle = (id) => {
    const { contentBundleState, booksState } = this.props;
    const { bundles } = this.state;
    const bundle = bundles.find((b) => b.id === id);
    if (!bundle || !this.isBundleValidToSave(bundle)) return;
    const { pulseBookID } = booksState.bookDetail;
    let payload;
    if (bundle.bundleId) {
      payload = this.buildBundleWithBundleIdPayload(bundle, booksState.bookDetail, pulseBookID);
    } else {
      payload = this.buildBundlePayload(bundle, booksState.bookDetail, pulseBookID);
    }
    contentBundleState.saveBundles([payload]);
  };

  handleSaveAll = () => {
    const { contentBundleState, booksState } = this.props;
    const { bundles } = this.state;
    const { pulseBookID } = booksState.bookDetail;
    const payload = bundles
      .filter((bundle) => this.isBundleValidToSave(bundle))
      .map((bundle) =>
        bundle.bundleId
          ? this.buildBundleWithBundleIdPayload(bundle, booksState.bookDetail, pulseBookID)
          : this.buildBundlePayload(bundle, booksState.bookDetail, pulseBookID)
      );
    if (payload.length > 0) {
      contentBundleState.saveBundles(payload);
    }
  };

  handleExport = () => {
    const { bundles } = this.state;
    const dataRows = bundles
      .filter((b) => b.startPoint || b.endPoint || b.bundleName || b.bundleId)
      .map((b) => {
        const uris = b.bundleUri && b.bundleUri.length > 0 ? b.bundleUri : [];
        const lastUri = uris.length > 1 ? uris[uris.length - 1] : uris[0];
        const startUri = uris[0] ? this.getFullHref(uris[0]) : b.startPoint;
        const endUri = lastUri ? this.getFullHref(lastUri) : b.endPoint;
        return { startPoint: startUri, endPoint: endUri, bundleName: b.bundleName, bundleId: b.bundleId };
      });
    if (dataRows.length === 0) return;
    const headers = [
      { name: 'Start Point', key: 'startPoint' },
      { name: 'End Point', key: 'endPoint' },
      { name: 'Bundle Name', key: 'bundleName' },
      { name: 'Bundle ID', key: 'bundleId' }
    ];
    const csv = utils.buildCSVWithHeaders(dataRows, headers);
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const hh = String(today.getHours()).padStart(2, '0');
    const min = String(today.getMinutes()).padStart(2, '0');
    const ss = String(today.getSeconds()).padStart(2, '0');
    utils.downloadCSV(csv, `Content_Bundles_${yyyy}${mm}${dd}_${hh}${min}${ss}.csv`);
  };

  handleCancel = () => {
    window.history.back();
  };

  render() {
    const { classes, language, booksStatus, match, contentBundleState } = this.props;
    const { bundles, tocModalOpen, tocData } = this.state;
    const hasExportData = bundles.some((b) => b.startPoint || b.endPoint || b.bundleName || b.bundleId);
    const hasValidBundlesToSave = bundles.some((b) => this.isBundleValidToSave(b));
    const hasIncompleteBundles = bundles.some((b) => this.isBundleIncomplete(b));
    const tocForModal =
      contentBundleState.tocData && contentBundleState.tocData.length > 0 ? contentBundleState.tocData : tocData;

    return (
      <LoadingHandler
        loading={booksStatus.isPending}
        isError={booksStatus.isError}
        loadingContent={<Loader />}
        errorContent={<StateErrorDisplay error={booksStatus.error} showDetails />}
        content={
          <div className={classes.root}>
            <Breadcrumbs
              data={<FormattedMessage {...language.getText('label.CONTENT_BUNDLES')} />}
              id={match.params.bookid}
            />
            <Paper className={classes.paper}>
              <Typography variant="h5" className={classes.title}>
                <FormattedMessage {...language.getText('label.CONTENT_BUNDLES')} />
              </Typography>
              <Typography variant="body2" className={classes.subtitle}>
                To retrieve Content Bundles for this title please first click <strong>Save</strong> and then select the{' '}
                <strong>Export</strong> button below.
              </Typography>

              <TableContainer className={classes.tableContainer}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeader}>No.</TableCell>
                      <TableCell className={classes.tableHeader}>Add/Delete</TableCell>
                      <TableCell className={classes.tableHeader}>Start Point</TableCell>
                      <TableCell className={classes.tableHeader}>End Point</TableCell>
                      <TableCell className={classes.tableHeader}>Bundle Name</TableCell>
                      <TableCell className={classes.tableHeader}>Bundle ID</TableCell>
                      <TableCell className={classes.tableHeader}>Save</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bundles.map((bundle, index) => (
                      <TableRow key={bundle.id}>
                        <TableCell>{index === bundles.length - 1 ? 'Next' : index + 1}</TableCell>
                        <TableCell>
                          {index === bundles.length - 1 ? (
                            <IconButton onClick={this.handleAddBundle} className={classes.addButton} size="small">
                              <AddIcon />
                            </IconButton>
                          ) : (
                            <IconButton
                              onClick={() => this.handleDeleteBundle(bundle.id)}
                              size="small"
                              className={classes.deleteButton}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            placeholder="Click to enter start point"
                            value={bundle.startPoint}
                            onClick={() => this.handleOpenTocModal(bundle.id, 'start')}
                            variant="outlined"
                            size="small"
                            className={classes.inputField}
                            InputProps={{
                              readOnly: true
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            placeholder="Click to enter end point"
                            value={bundle.endPoint}
                            onClick={() => this.handleOpenTocModal(bundle.id, 'end')}
                            variant="outlined"
                            size="small"
                            className={classes.inputField}
                            InputProps={{
                              readOnly: true
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            placeholder="Bundle Name"
                            value={bundle.bundleName}
                            onChange={(e) => this.handleInputChange(bundle.id, 'bundleName', e.target.value)}
                            variant="outlined"
                            size="small"
                            className={classes.inputField}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={bundle.bundleId}
                            onChange={(e) => this.handleInputChange(bundle.id, 'bundleId', e.target.value)}
                            variant="outlined"
                            size="small"
                            className={`${classes.inputField} ${classes.bundleIdField}`}
                            disabled
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            className={classes.saveButton}
                            onClick={() => this.handleSaveBundle(bundle.id)}
                            size="small"
                            disabled={!this.isBundleValidToSave(bundle)}
                          >
                            Save
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box className={classes.actionButtons}>
                <Button
                  variant="contained"
                  className={classes.exportButton}
                  onClick={this.handleExport}
                  disabled={!hasExportData}
                >
                  Export
                </Button>
                <Box>
                  <Button variant="outlined" className={classes.cancelButton} onClick={this.handleCancel}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    className={classes.saveButton}
                    onClick={this.handleSaveAll}
                    disabled={!hasValidBundlesToSave || hasIncompleteBundles}
                  >
                    Save All Content Bundles
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* TOC Modal */}
            <Dialog open={tocModalOpen} onClose={this.handleCloseTocModal} classes={{ paper: classes.dialog }}>
              <DialogTitle className={classes.dialogTitle} disableTypography>
                <Typography className={classes.dialogTitleText}>Select a Start/End Point</Typography>
                <IconButton onClick={this.handleCloseTocModal} className={classes.closeButton}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DialogTitle>
              <DialogContent className={classes.dialogContentRoot}>
                <List className={classes.tocList}>
                  {(tocForModal || []).map((item, index) => (
                    <React.Fragment key={item.id || item.href || `toc-${index}`}>
                      <ListItem button className={classes.tocItem} onClick={() => this.handleSelectTocItem(item.title)}>
                        <ListItemText
                          primary={
                            <div className={classes.tocItemText}>
                              <span className={classes.tocItemNumber}>{index + 1}.</span>
                              <span style={{ fontSize: '16px', color: '#000' }}>{item.title}</span>
                            </div>
                          }
                        />
                      </ListItem>
                      {item.children &&
                        item.children.map((child, childIndex) => (
                          <ListItem
                            button
                            key={child.id || child.href || `toc-child-${index}-${childIndex}`}
                            className={classes.tocSubItem}
                            onClick={() => this.handleSelectTocItem(child.title)}
                          >
                            <ListItemText
                              primary={
                                <div className={classes.tocItemText}>
                                  <span className={classes.tocItemNumber}>{childIndex + 1}.</span>
                                  <span style={{ fontSize: '16px', color: '#000' }}>{child.title}</span>
                                </div>
                              }
                            />
                          </ListItem>
                        ))}
                    </React.Fragment>
                  ))}
                </List>
              </DialogContent>
            </Dialog>
          </div>
        }
      />
    );
  }
}

export default withStyles(styles)(observer(inject('language')(ContentBundles)));
